import BadgeService from '@/services/BadgeService'

/**
 * Débloque un badge via l'API backend
 * @param {number} badgeId - ID du badge à débloquer
 * @returns {Promise<boolean>} true si le badge a été débloqué avec succès
 */
export async function unlockBadge(badgeId) {
    try {
      // Débloquer le badge via l'API backend
      const unlockedBadge = await BadgeService.unlockBadge(badgeId)
      console.log(`Badge ${badgeId} débloqué !`, unlockedBadge)
      return true
    } catch (error) {
      console.error('Erreur lors du déverrouillage du badge:', error)

      // Fallback: utiliser localStorage en cas d'erreur API
      try {
        const savedBadges = localStorage.getItem('userBadges');
        let badges = [];

        if (savedBadges) {
          badges = JSON.parse(savedBadges);
        }

        const badgeIndex = badges.findIndex(b => b.id === badgeId);

        if (badgeIndex === -1) {
          badges.push({
            id: badgeId,
            unlocked: true,
            dateUnlocked: new Date().toISOString().split('T')[0]
          });
          console.log(`Badge ${badgeId} débloqué (fallback localStorage) !`);
        } else if (!badges[badgeIndex].unlocked) {
          badges[badgeIndex].unlocked = true;
          badges[badgeIndex].dateUnlocked = new Date().toISOString().split('T')[0];
          console.log(`Badge ${badgeId} débloqué (fallback localStorage) !`);
        } else {
          return false;
        }

        localStorage.setItem('userBadges', JSON.stringify(badges));
        return true;
      } catch (fallbackError) {
        console.error('Erreur lors du fallback localStorage:', fallbackError);
        return false;
      }
    }
  }

  /**
   * Vérifie si un badge est débloqué via l'API backend
   * @param {number} badgeId - ID du badge à vérifier
   * @returns {Promise<boolean>} true si le badge est débloqué
   */
  export async function isBadgeUnlocked(badgeId) {
    try {
      const badge = await BadgeService.getBadge(badgeId)
      return badge && badge.unlocked
    } catch (error) {
      console.error('Erreur lors de la vérification du badge:', error)

      // Fallback: vérifier dans localStorage
      try {
        const savedBadges = localStorage.getItem('userBadges');
        if (!savedBadges) return false;

        const badges = JSON.parse(savedBadges);
        const badge = badges.find(b => b.id === badgeId);

        return badge && badge.unlocked;
      } catch (fallbackError) {
        console.error('Erreur lors du fallback localStorage:', fallbackError);
        return false;
      }
    }
  }

  /**
   * Récupère tous les badges débloqués via l'API backend
   * @returns {Promise<Array>} Liste des badges débloqués
   */
  export async function getUnlockedBadges() {
    try {
      const { badges } = await BadgeService.getUserBadges()
      return badges.filter(b => b.unlocked)
    } catch (error) {
      console.error('Erreur lors de la récupération des badges:', error)

      // Fallback: récupérer depuis localStorage
      try {
        const savedBadges = localStorage.getItem('userBadges');
        if (!savedBadges) return [];

        const badges = JSON.parse(savedBadges);
        return badges.filter(b => b.unlocked);
      } catch (fallbackError) {
        console.error('Erreur lors du fallback localStorage:', fallbackError);
        return [];
      }
    }
  }

export function resetBadge(badgeId) {
  try {
    // Récupérer les badges existants
    const savedBadges = localStorage.getItem('userBadges');
    if (!savedBadges) {
      console.log('Aucun badge trouvé en stockage.');
      return false;
    }
    
    const badges = JSON.parse(savedBadges);
    
    // Trouver le badge spécifique
    const badgeIndex = badges.findIndex(b => b.id === badgeId);
    
    if (badgeIndex === -1) {
      console.log(`Badge ${badgeId} non trouvé.`);
      return false;
    }
    
    // Vérifier si le badge est déjà verrouillé
    if (!badges[badgeIndex].unlocked) {
      console.log(`Badge ${badgeId} déjà verrouillé.`);
      return false;
    }
    
    // Verrouiller le badge
    badges[badgeIndex].unlocked = false;
    badges[badgeIndex].dateUnlocked = null;
    
    // Sauvegarder les badges mis à jour
    localStorage.setItem('userBadges', JSON.stringify(badges));
    console.log(`Badge ${badgeId} verrouillé avec succès.`);
    return true;
    
  } catch (error) {
    console.error('Erreur lors du verrouillage du badge:', error);
    return false;
  }
}

export function resetBadges(badgeIds = null) {
  try {
    // Récupérer les badges existants
    const savedBadges = localStorage.getItem('userBadges');
    if (!savedBadges) {
      console.log('Aucun badge trouvé en stockage.');
      return false;
    }
    
    const badges = JSON.parse(savedBadges);
    let changesMade = false;
    
    // Mise à jour des badges
    const updatedBadges = badges.map(badge => {
      // Si badgeIds est fourni, vérifier si ce badge doit être réinitialisé
      if (badgeIds === null || badgeIds.includes(badge.id)) {
        if (badge.unlocked) {
          changesMade = true;
          return {
            ...badge,
            unlocked: false,
            dateUnlocked: null
          };
        }
      }
      return badge;
    });
    
    // Si aucun changement n'a été effectué, retourner false
    if (!changesMade) {
      console.log('Aucun badge déverrouillé à réinitialiser.');
      return false;
    }
    
    // Sauvegarder les badges mis à jour
    localStorage.setItem('userBadges', JSON.stringify(updatedBadges));
    console.log(`Badges verrouillés avec succès.`);
    return true;
    
  } catch (error) {
    console.error('Erreur lors du verrouillage des badges:', error);
    return false;
  }
}